import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, inject } from '@angular/core';
import { AutoResume, DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { leaveWithoutWarning } from 'src/app/core/guards/unsaved-changes.guard';
import { InactivityChoice, InactivityComponent } from 'src/app/shared/modals/inactivity/inactivity.component';

/** The `/profile/self` values (ApplicationSettings.AutoLogout*), in seconds. */
export interface SessionIdleSettings {
  autoLogoutIdleTime?: number;
  autoLogoutTimeout?: number;
  autoLogoutKeepaliveInterval?: number;
}

/** Light authenticated request whose cookie check also slides the session. */
const KEEPALIVE_URL = '/api/auth/isAuthenticated';
const LOGOUT_URL = '/api/auth/logout?returnUrl=';

/**
 * Logs an idle user out, as Frodo does: after `autoLogoutIdleTime` without mouse, keyboard or scroll
 * activity a warning counts `autoLogoutTimeout` down, then the user is logged out — without the
 * unsaved-input dialog, which nobody would answer. While the user is active, a keepalive request every
 * `autoLogoutKeepaliveInterval` slides the server session, so it cannot expire while a long form is
 * being typed (Frodo sets the interval but no request). `autoLogoutIdleTime` 0 turns all of it off.
 */
@Injectable({ providedIn: 'root' })
export class SessionIdleService {
  private readonly idle = inject(Idle);
  private readonly keepalive = inject(Keepalive);
  private readonly modalService = inject(NgbModal);
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);

  private started = false;
  private warning: NgbModalRef | null = null;

  start(settings: SessionIdleSettings): void {
    const idleSeconds = settings.autoLogoutIdleTime ?? 0;
    if (this.started || idleSeconds <= 0) return;
    this.started = true;

    this.idle.setIdle(idleSeconds);
    this.idle.setTimeout(settings.autoLogoutTimeout ?? 60);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    // Once the warning is up, only its buttons answer it. With ng-idle's default (AutoResume.idle) the
    // first mouse move ended the idle state and closed the warning, so reaching for "Ostani prijavljen"
    // made it vanish before it could be read or clicked.
    this.idle.setAutoResume(AutoResume.notIdle);
    this.keepalive.interval(settings.autoLogoutKeepaliveInterval ?? 15);
    this.keepalive.request(KEEPALIVE_URL);

    // ng-idle ticks outside the Angular zone; the modal must be opened and updated inside it to paint.
    this.idle.onIdleStart.subscribe(() => this.zone.run(() => this.openWarning()));
    this.idle.onTimeoutWarning.subscribe((secondsLeft: number) =>
      this.zone.run(() => (this.warning?.componentInstance as InactivityComponent | undefined)?.secondsLeft.set(secondsLeft)));
    // The idle state ends without this tab's buttons when the user works in another portal tab (the
    // tabs share the expiry), so this tab's warning goes too.
    this.idle.onIdleEnd.subscribe(() => this.zone.run(() => this.closeWarning()));
    this.idle.onTimeout.subscribe(() => this.zone.run(() => {
      this.closeWarning();
      leaveWithoutWarning(this.document.location, LOGOUT_URL + encodeURIComponent(this.document.location.href));
    }));

    this.idle.watch();
  }

  private openWarning(): void {
    if (this.warning) return;
    const ref = this.modalService.open(InactivityComponent, { size: 'md', backdrop: 'static', keyboard: false });
    this.warning = ref;
    ref.result.then(
      (choice: InactivityChoice) => this.zone.run(() => {
        this.warning = null;
        if (choice === 'logout') {
          // The user's own choice: the same as the header's Odjava, so unsaved input is still asked about.
          this.idle.stop();
          this.document.location.href = LOGOUT_URL + encodeURIComponent(this.document.location.href);
          return;
        }
        this.idle.watch();
      }),
      () => this.zone.run(() => (this.warning = null)),
    );
  }

  private closeWarning(): void {
    this.warning?.dismiss();
    this.warning = null;
  }
}
